// Client-facing document label derivation for the public /o/:uuid view.
//
// A quote-stage order is a proposal the client sees as an "Estimate". Once it
// is committed, the customer is looking at their "Quote" — and it STAYS
// a Quote for every downstream stage (in_production, shipped, invoiced,
// closed). The original view only special-cased committed/in_production, so a
// shipped or invoiced order wrongly relabeled back to "Estimate"
// (CEO 2026-07-24: the SO is the final step for the customer to view their
// order in detail).
//
// `cancelled` orders are never served publicly (the route 404s them), so they
// have no client-facing label.
//
// DB-free + pure so the offline QA gate can EXECUTE this derivation directly
// instead of regex-matching the component source. Same discipline as
// commission-margin.ts.

/**
 * True for any post-commit stage — the point at which the client's document is
 * a Quote rather than an Estimate. `quote` (and the never-served
 * `cancelled`) are false.
 */
export function isSalesOrderStage(statusStage: string): boolean {
  return (
    statusStage === "committed" ||
    statusStage === "in_production" ||
    statusStage === "shipped" ||
    statusStage === "invoiced" ||
    statusStage === "closed"
  );
}

/** Client-facing document noun: "Quote" post-commit, else "Estimate". */
// CEO 2026-10-15: Estimate and Sales Order are ONE document now — the Quote —
// at every stage. The stage still decides what the quote can do (send,
// approve, POs, invoice); it no longer changes what people call it.
export function deriveDocLabel(_statusStage: string): "Quote" {
  return "Quote";
}

/** Lowercase noun for CTA copy ("Approve estimate" / "Approve order"). */
export function deriveDocNoun(_statusStage: string): "quote" {
  return "quote";
}

/**
 * True for the stage range on which a client sign-off ("Approve Quote" on
 * the share page) or a rep offline "Mark approved" can be RECORDED — every
 * post-commit stage EXCEPT the terminal `closed` (2026-07-26).
 *
 * THE single source of truth for that range: the public POST /o/:uuid/approve
 * SO branch, the staff POST /orders/:id/mark-approved endpoint, and the client
 * PublicOrderPage "Approve Quote" render are ALL driven from this one
 * predicate, so the button the client sees and the stage the server accepts can
 * never drift — the drift that produced a 409 "refresh the page" retry loop when
 * the render covered in_production/shipped/invoiced but the handler gated on
 * `committed` alone. DB-free + pure so the offline QA gate executes it directly.
 */
export function isSoSignoffReachable(statusStage: string): boolean {
  return isSalesOrderStage(statusStage) && statusStage !== "closed";
}
